import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add,
  ContentCopy,
  Delete,
  DragIndicator,
  Edit,
} from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { firmIntelligenceUrl } from 'common/config/urls';
import {
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY,
} from 'common/constants/services/firms';
import { useCurrencySelector } from 'common/hooks';
import {
  ColType,
  COLUMN_TYPES,
  DEFAULT_FIRM_SUMMARY_COLUMNS,
  Filter,
  FilterOp,
  getValueTypesForSummary,
  normalizeFirmSpreadsheetDefinition,
  SortKey,
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryGroupBy,
} from 'common/types/summaryDefinitions';
import { DisplayFormatType } from 'common/types/summaryDefinitions/valueTypes/displayFormats';
import CurrencyMenu from 'components/CurrencyMenu';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type {
  TertiaryNavigationActionControlConfig,
  TertiaryNavigationLeftActionControlConfig,
} from 'context/FirmBaseLayoutContext';
import { OrderedDefinition, useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY } from 'pages/Firms/common/constants';
import { SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES } from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import EditColumnsPanel from 'pages/Firms/pages/FirmSummary/EditColumnsPanel';
import {
  FilterOpOption,
  getDisplayFormatTypeForColumn,
  getFilterOpsForColumnType,
  isOpValidForFormatType,
  validateFilterValue,
} from 'pages/Firms/pages/FirmSummary/filterValidation';
import FirmSummaryBody from './FirmSummaryBody';
import { useIntelligenceContext } from './IntelligenceContext';
import {
  buildIntelligenceTertiaryNavigationItems,
  getCustomViewSlug,
  getDefaultFirmSummaryDefinition,
  parseCustomViewSlug,
} from './intelligenceNavigation';
import {
  INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS,
  INTELLIGENCE_TERTIARY_NAVIGATION_LABELS,
  INTELLIGENCE_TERTIARY_NAVIGATION_SLUGS,
} from './intelligenceNavigation.constants';

const INTELLIGENCE_BASE_URL_FN = (slug: string) =>
  `${firmIntelligenceUrl(slug)}/${INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES}`;

interface FilterValueInputProps {
  value: string | number;
  formatType: DisplayFormatType;
  disabled?: boolean;
  onChange: (value: string) => void;
  onValidationChange: (valid: boolean) => void;
}

const FilterValueInput: FC<FilterValueInputProps> = ({
  value,
  formatType,
  disabled,
  onChange,
  onValidationChange,
}) => {
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const validation = validateFilterValue(localValue, formatType);

  useEffect(() => {
    setLocalValue(String(value ?? ''));
  }, [value, formatType]);

  const handleTextChange = (newVal: string) => {
    setLocalValue(newVal);
    onValidationChange(validateFilterValue(newVal, formatType).valid);
  };

  if (formatType === 'boolean') {
    return (
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Value</InputLabel>
        <Select
          label="Value"
          value={localValue}
          onChange={event => {
            setLocalValue(event.target.value);
            onChange(event.target.value);
            onValidationChange(true);
          }}
          disabled={disabled}>
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      label="Value"
      size="small"
      type={formatType === 'date' ? 'date' : 'text'}
      value={localValue}
      onChange={event => handleTextChange(event.target.value)}
      onBlur={() => onChange(localValue)}
      disabled={disabled}
      error={!validation.valid}
      helperText={validation.error}
      InputLabelProps={formatType === 'date' ? { shrink: true } : undefined}
      sx={{ width: 150 }}
    />
  );
};

const generateCloneName = (sourceName: string, existingNames: string[]): string => {
  const baseName = sourceName.replace(/\s*\(Copy(?:\s+\d+)?\)\s*$/i, '').trim();
  const nameSet = new Set(existingNames.map(n => n.toLowerCase()));

  const firstCandidate = `${baseName} (Copy)`;
  if (!nameSet.has(firstCandidate.toLowerCase())) {
    return firstCandidate;
  }

  let counter = 2;
  while (nameSet.has(`${baseName} (Copy ${counter})`.toLowerCase())) {
    counter += 1;
  }
  return `${baseName} (Copy ${counter})`;
};

const SortableListItem: FC<{
  id: string;
  children: React.ReactNode;
  secondaryAction?: React.ReactNode;
  disabled?: boolean;
}> = ({ id, children, secondaryAction, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: `${transition}, transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={secondaryAction}
      sx={{
        alignItems: 'flex-start',
        borderRadius: 1,
        transition: 'background-color 150ms ease',
        ...(isDragging && {
          backgroundColor: 'action.hover',
          boxShadow: 2,
        }),
      }}>
      <ListItemIcon
        sx={{
          minWidth: 32,
          alignSelf: 'center',
          cursor: disabled ? 'not-allowed' : 'grab',
          color: 'text.secondary',
          borderRadius: 1,
          transition: 'background-color 150ms ease',
          ...(!disabled && {
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }),
        }}
        {...(disabled ? {} : { ...attributes, ...listeners })}>
        <DragIndicator fontSize="small" />
      </ListItemIcon>
      {children}
    </ListItem>
  );
};

interface FirmSummaryWorkspaceProps {
  firmSlug: string;
  activeTertiaryTabSlug: string;
  baseUrlFn?: (firmSlug: string) => string;
  navigationSlugs?: { FIRM_SUMMARY: string };
  navigationLabels?: { FIRM_SUMMARY: string };
  navigationAriaLabel?: string;
  summaryType?: 'firm' | 'valuations';
}

const FirmSummaryWorkspace: FC<FirmSummaryWorkspaceProps> = ({
  firmSlug,
  activeTertiaryTabSlug,
  baseUrlFn = INTELLIGENCE_BASE_URL_FN,
  navigationSlugs = INTELLIGENCE_TERTIARY_NAVIGATION_SLUGS,
  navigationLabels = INTELLIGENCE_TERTIARY_NAVIGATION_LABELS,
  navigationAriaLabel = 'Intelligence views',
  summaryType = 'firm',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const sortableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const {
    orderedDefinitions,
    activeDefinition,
    setActiveDefinition,
    createDefinition,
    cloneDefinition,
    deleteDefinition,
    updateDefinition,
    refetch,
    isLoading,
    isSaving,
  } = useSummaryDefinitions();

  const defaultDefinition = useMemo(
    () => getDefaultFirmSummaryDefinition(orderedDefinitions),
    [orderedDefinitions]
  );

  // Resolve which definition the current tertiary slug refers to.
  const slugDefinition = useMemo<OrderedDefinition | null>(() => {
    if (activeTertiaryTabSlug === navigationSlugs.FIRM_SUMMARY) {
      return defaultDefinition;
    }
    const definitionId = parseCustomViewSlug(activeTertiaryTabSlug);
    if (definitionId === null) {
      return null;
    }
    return orderedDefinitions.find(definition => definition.id === definitionId) ?? null;
  }, [activeTertiaryTabSlug, defaultDefinition, navigationSlugs.FIRM_SUMMARY, orderedDefinitions]);

  // Keep the context's active definition in sync with the URL.
  useEffect(() => {
    if (slugDefinition && slugDefinition.id !== activeDefinition?.id) {
      setActiveDefinition(slugDefinition.id);
    }
  }, [slugDefinition, activeDefinition?.id, setActiveDefinition]);

  // When the firm has no summary definitions yet (no system global, no custom views),
  // seed a firm-owned "Firm Summary" definition with the default columns so the workspace
  // never lands on an empty body. Guarded by a ref so we only fire once per mount.
  const autoSeedFiredRef = useRef(false);
  useEffect(() => {
    if (isLoading || isSaving || autoSeedFiredRef.current) {
      return;
    }
    if (orderedDefinitions.length > 0) {
      return;
    }
    autoSeedFiredRef.current = true;
    createDefinition(navigationLabels.FIRM_SUMMARY);
  }, [createDefinition, isLoading, isSaving, navigationLabels, orderedDefinitions.length]);

  // If the URL points at a custom view that no longer exists (e.g. after delete),
  // redirect back to the default Firm Summary tab once the definitions list is loaded.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (activeTertiaryTabSlug === navigationSlugs.FIRM_SUMMARY) {
      return;
    }
    const definitionId = parseCustomViewSlug(activeTertiaryTabSlug);
    if (definitionId === null || !orderedDefinitions.some(definition => definition.id === definitionId)) {
      navigate(
        {
          pathname: `${baseUrlFn(firmSlug)}/${navigationSlugs.FIRM_SUMMARY}`,
          search: location.search,
        },
        { replace: true }
      );
    }
  }, [activeTertiaryTabSlug, baseUrlFn, firmSlug, isLoading, location.search, navigate, navigationSlugs, orderedDefinitions]);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuDefinitionId, setMenuDefinitionId] = useState<number | null>(null);
  const [isColumnEditorOpen, setIsColumnEditorOpen] = useState(false);
  const [isCreatingNewView, setIsCreatingNewView] = useState(false);
  const [editorName, setEditorName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>([]);
  const [editorGroupBy, setEditorGroupBy] = useState<SummaryGroupBy>('company');
  const [editorSortKeys, setEditorSortKeys] = useState<SortKey[]>([]);
  const [editorFilters, setEditorFilters] = useState<Filter[]>([]);
  const [filterInputValidities, setFilterInputValidities] = useState<boolean[]>([]);
  const [editorBreakevenMultiple, setEditorBreakevenMultiple] = useState<string>('1');
  const [isEditorSaving, setIsEditorSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ columnType: ColType; dependents: ColType[] } | null>(null);

  const availableValueTypes = useMemo(() => getValueTypesForSummary('firm'), []);
  const valueTypeById = useMemo(
    () => new Map(availableValueTypes.map(valueType => [valueType.id, valueType])),
    [availableValueTypes]
  );
  const requiredColumnIds = useMemo(
    () => availableValueTypes.filter(valueType => valueType.metadata?.isRequired).map(valueType => valueType.id),
    [availableValueTypes]
  );

  const requiredColumnsForEditor = useMemo(
    () => new Set([...requiredColumnIds, 'company_name', 'fund_name']),
    [requiredColumnIds]
  );

  const hasBreakevenColumn = useMemo(
    () => editorColumns.includes(COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE),
    [editorColumns]
  );
  const breakevenMultipleValue = Number(editorBreakevenMultiple);
  const isBreakevenMultipleInvalid =
    hasBreakevenColumn && (!Number.isFinite(breakevenMultipleValue) || breakevenMultipleValue <= 0);
  const filterOptionsPerFilter = useMemo<FilterOpOption[][]>(
    () =>
      editorFilters.map(([column]) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
        return getFilterOpsForColumnType(formatType);
      }),
    [editorFilters, valueTypeById]
  );

  const filterValidations = useMemo(
    () =>
      editorFilters.map(([column, [, value]]) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
        return validateFilterValue(value, formatType);
      }),
    [editorFilters, valueTypeById]
  );

  const hasInvalidFilters = useMemo(
    () => filterValidations.some(v => !v.valid) || filterInputValidities.some(v => !v),
    [filterValidations, filterInputValidities]
  );

  const handleFilterValidationChange = useCallback((index: number, valid: boolean) => {
    setFilterInputValidities(prev => prev.map((v, idx) => (idx === index ? valid : v)));
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuDefinitionId(null);
  }, []);

  const getOrderedColumnTypes = useCallback((definition?: SpreadsheetDefinition): ColType[] => {
    if (!definition?.columns || Object.keys(definition.columns).length === 0) {
      return DEFAULT_FIRM_SUMMARY_COLUMNS;
    }

    return Object.entries(definition.columns)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, colType]) => colType);
  }, []);

  const canEditDefinition = useCallback(
    (definition?: SummaryDefinition | OrderedDefinition | null) => {
      if (!definition) {
        return false;
      }
      if ('isGlobal' in definition && definition.isGlobal) {
        return Boolean(definition.firm || definition.fund);
      }
      return true;
    },
    []
  );

  const openColumnEditor = useCallback(
    (definitionToEdit: SummaryDefinition | OrderedDefinition | null = activeDefinition) => {
      if (!definitionToEdit || !canEditDefinition(definitionToEdit)) {
        return;
      }

      const currentDefinition = normalizeFirmSpreadsheetDefinition(
        definitionToEdit.spreadsheet_definition ?? definitionToEdit.spreadsheetDefinition
      );
      const orderedColumns = getOrderedColumnTypes(currentDefinition);
      const withRequired = [...orderedColumns];

      ['company_name', ...requiredColumnIds].forEach(requiredId => {
        if (!withRequired.includes(requiredId)) {
          withRequired.unshift(requiredId);
        }
      });

      setEditorColumns(withRequired);
      setEditorGroupBy(currentDefinition.groupBy ?? 'company');
      setEditorSortKeys(currentDefinition.sortKeys ?? []);
      const initialFilters = currentDefinition.filters ?? [];
      setEditorFilters(initialFilters);
      setFilterInputValidities(initialFilters.map(() => true));
      setEditorBreakevenMultiple(`${currentDefinition.breakevenMultiple ?? 1}`);
      setEditorName(definitionToEdit.name ?? '');
      setIsCreatingNewView(false);
      setIsColumnEditorOpen(true);
    },
    [activeDefinition, canEditDefinition, getOrderedColumnTypes, requiredColumnIds]
  );

  const closeColumnEditor = useCallback(() => {
    setIsColumnEditorOpen(false);
    setIsCreatingNewView(false);
    setEditorName('');
    setEditorColumns([]);
    setEditorGroupBy('company');
    setEditorSortKeys([]);
    setEditorFilters([]);
    setFilterInputValidities([]);
    setEditorBreakevenMultiple('1');
    setIsEditorSaving(false);
    setRemoveConfirm(null);
  }, []);

  const openCreateDialog = useCallback(() => {
    const existingNames = orderedDefinitions.map(d => d.name);
    let newName = 'New View';
    if (existingNames.some(n => n.toLowerCase() === newName.toLowerCase())) {
      newName = generateCloneName(newName, existingNames);
    }
    setEditorColumns([...DEFAULT_FIRM_SUMMARY_COLUMNS]);
    setEditorGroupBy('company');
    setEditorSortKeys([]);
    setEditorFilters([]);
    setFilterInputValidities([]);
    setEditorBreakevenMultiple('1');
    setEditorName(newName);
    setIsCreatingNewView(true);
    setIsColumnEditorOpen(true);
  }, [orderedDefinitions]);

  const handleCloneDefinition = useCallback(
    async (definitionId: number) => {
      if (isSaving) {
        return;
      }
      const definitionToClone = orderedDefinitions.find(d => d.id === definitionId);
      if (!definitionToClone) {
        return;
      }
      handleMenuClose();
      const existingNames = orderedDefinitions.map(d => d.name);
      const clonedDefinition = await cloneDefinition(
        definitionId,
        generateCloneName(definitionToClone.name, existingNames)
      );
      if (clonedDefinition) {
        navigate({
          pathname: `${baseUrlFn(firmSlug)}/${getCustomViewSlug(clonedDefinition.id)}`,
          search: location.search,
        });
      }
    },
    [baseUrlFn, cloneDefinition, firmSlug, handleMenuClose, isSaving, location.search, navigate, orderedDefinitions]
  );

  const handleDeleteDefinition = useCallback(
    async (definitionId: number) => {
      handleMenuClose();
      await deleteDefinition(definitionId);
      // The redirect-on-stale-slug effect will return the user to firm-summary if the
      // deleted definition was active; nothing else to do here.
    },
    [deleteDefinition, handleMenuClose]
  );

  const sortIds = useMemo(() => editorSortKeys.map((_, i) => `sort-${i}`), [editorSortKeys]);
  const filterIds = useMemo(() => editorFilters.map((_, i) => `filter-${i}`), [editorFilters]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleReorderSortKeys = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = sortIds.indexOf(String(active.id));
      const newIndex = sortIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorSortKeys(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [sortIds]
  );

  const handleReorderFilters = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = filterIds.indexOf(String(active.id));
      const newIndex = filterIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorFilters(prev => arrayMove(prev, oldIndex, newIndex));
      setFilterInputValidities(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [filterIds]
  );

  const handleAddSortKey = useCallback(() => {
    const defaultColumn = editorColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorSortKeys(prev => [...prev, [defaultColumn, 'asc']]);
  }, [editorColumns]);

  const handleUpdateSortColumn = useCallback((index: number, column: ColType) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [column, sortKey[1]] : sortKey)));
  }, []);

  const handleUpdateSortDirection = useCallback((index: number, direction: SortKey[1]) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [sortKey[0], direction] : sortKey)));
  }, []);

  const handleRemoveSortKey = useCallback((index: number) => {
    setEditorSortKeys(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleAddFilter = useCallback(() => {
    const defaultColumn = editorColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorFilters(prev => [...prev, [defaultColumn, ['=', '']] as Filter]);
    setFilterInputValidities(prev => [...prev, true]);
  }, [editorColumns]);

  const handleUpdateFilterColumn = useCallback(
    (index: number, column: ColType) => {
      setEditorFilters(prev =>
        prev.map((filter, idx) => {
          if (idx !== index) {
            return filter;
          }
          const [, [currentOp, currentValue]] = filter;
          const newFormatType = getDisplayFormatTypeForColumn(column, valueTypeById);
          const validOps = getFilterOpsForColumnType(newFormatType);
          const nextOp = isOpValidForFormatType(currentOp, newFormatType) ? currentOp : validOps[0].value;
          const valueValid = validateFilterValue(currentValue, newFormatType).valid;
          const nextValue = valueValid ? currentValue : '';
          return [column, [nextOp, nextValue]] as Filter;
        })
      );
    },
    [valueTypeById]
  );

  const handleUpdateFilterOp = useCallback((index: number, op: FilterOp) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [op, filter[1][1]]] : filter)));
  }, []);

  const handleUpdateFilterValue = useCallback((index: number, value: string | number) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [filter[1][0], value]] : filter)));
  }, []);

  const handleRemoveFilter = useCallback((index: number) => {
    setEditorFilters(prev => prev.filter((_, idx) => idx !== index));
    setFilterInputValidities(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (!removeConfirm) {
      return;
    }

    const affected = [removeConfirm.columnType, ...removeConfirm.dependents];
    setEditorColumns(prev => prev.filter(col => !affected.includes(col)));
    setRemoveConfirm(null);
  }, [removeConfirm]);

  const handleCancelRemove = useCallback(() => {
    setRemoveConfirm(null);
  }, []);

  const handleSaveColumns = useCallback(async () => {
    if (isEditorSaving || isBreakevenMultipleInvalid || hasInvalidFilters) {
      return;
    }

    const trimmedName = editorName.trim();
    if (!trimmedName) {
      return;
    }

    const validColumns = new Set(editorColumns);
    const sanitizedSortKeys = editorSortKeys.filter(([col]) => validColumns.has(col));
    const sanitizedFilters = editorFilters.filter(
      ([col, [, value]]) => validColumns.has(col) && value !== null && typeof value !== 'undefined' && `${value}` !== ''
    );

    const nextDefinition: SpreadsheetDefinition = {
      columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
      groupBy: editorGroupBy,
      sortKeys: sanitizedSortKeys,
      filters: sanitizedFilters,
      breakevenMultiple: hasBreakevenColumn ? breakevenMultipleValue : 1,
      filterExclusions: [],
    };

    try {
      setIsEditorSaving(true);

      let createdDefinitionId: number | null = null;

      if (isCreatingNewView) {
        const newDefinition = await createDefinition(trimmedName, nextDefinition);
        if (newDefinition) {
          createdDefinitionId = newDefinition.id;
        }
      } else if (activeDefinition) {
        const baseDefinition = normalizeFirmSpreadsheetDefinition(
          activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
        );
        await updateDefinition(activeDefinition.id, {
          name: trimmedName,
          spreadsheet_definition: { ...baseDefinition, ...nextDefinition },
        });
      }

      // Refetch before navigating so the new definition is already in orderedDefinitions
      // when the URL switches — otherwise the new tab and body show a perceptible lag.
      const definitionDataQueryKey = summaryType === 'valuations'
        ? FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY
        : FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY;
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] }),
      ]);

      if (createdDefinitionId !== null) {
        navigate({
          pathname: `${baseUrlFn(firmSlug)}/${getCustomViewSlug(createdDefinitionId)}`,
          search: location.search,
        });
      }

      closeColumnEditor();
    } finally {
      setIsEditorSaving(false);
    }
  }, [
    activeDefinition,
    baseUrlFn,
    breakevenMultipleValue,
    closeColumnEditor,
    createDefinition,
    editorColumns,
    editorFilters,
    editorGroupBy,
    editorName,
    editorSortKeys,
    firmSlug,
    hasBreakevenColumn,
    hasInvalidFilters,
    isBreakevenMultipleInvalid,
    isCreatingNewView,
    isEditorSaving,
    location.search,
    navigate,
    queryClient,
    refetch,
    summaryType,
    updateDefinition,
  ]);

  // Kebab menu — anchored to the tertiary nav item's "more" button.
  const menuDefinition = useMemo<OrderedDefinition | null>(() => {
    if (menuDefinitionId === null) {
      return null;
    }
    return orderedDefinitions.find(definition => definition.id === menuDefinitionId) ?? null;
  }, [menuDefinitionId, orderedDefinitions]);

  const handleItemMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: { value: string }) => {
      event.stopPropagation();
      const definitionForSlug =
        item.value === navigationSlugs.FIRM_SUMMARY
          ? defaultDefinition
          : orderedDefinitions.find(definition => definition.id === parseCustomViewSlug(item.value));
      if (!definitionForSlug) {
        return;
      }
      setMenuDefinitionId(definitionForSlug.id);
      setMenuAnchor(event.currentTarget);
    },
    [defaultDefinition, navigationSlugs.FIRM_SUMMARY, orderedDefinitions]
  );

  // Register tertiary navigation items + add-view action + kebab handler with the layout shell.
  // Memoize each part so the shell only re-registers when meaningful state changes.
  const tertiaryNavItems = useMemo(
    () => buildIntelligenceTertiaryNavigationItems(firmSlug, orderedDefinitions, location.search, baseUrlFn, navigationSlugs, navigationLabels),
    [baseUrlFn, firmSlug, location.search, navigationLabels, navigationSlugs, orderedDefinitions]
  );

  const tertiaryNavLeftActions = useMemo<TertiaryNavigationLeftActionControlConfig[]>(
    () => [
      {
        key: 'add-view',
        onClick: openCreateDialog,
        type: 'addButton',
      },
    ],
    [openCreateDialog]
  );

  // Currency/units selector on the right side of the tertiary nav (PRISM design).
  // The unit toggle re-scales the summary numbers; clicking the currency code opens
  // CurrencyMenu, and a selection re-fetches the exchange rate for the measurement
  // date registered in the layout page filters (via useFirmMeasurementDateFilter).
  const { format, formatDispatch } = useIntelligenceContext();
  const {
    isCurrencyLoading,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    unitDisplayLabel,
    currencyDisplayLabel,
    currencyTooltipTitle,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  } = useCurrencySelector({
    format,
    dispatch: formatDispatch,
    fallbackSourceCurrency: FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
  });

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(
    () => [
      {
        type: 'currencySelector',
        currency: currencyDisplayLabel,
        currencyTooltipTitle,
        unit: unitDisplayLabel,
        onCurrencyClick: handleCurrencyMenuOpen,
        onUnitClick: handleUnitClick,
      },
    ],
    [currencyDisplayLabel, currencyTooltipTitle, handleCurrencyMenuOpen, handleUnitClick, unitDisplayLabel]
  );

  const tertiaryNavigationState = useMemo(
    () => ({
      ariaLabel: navigationAriaLabel,
      actions: tertiaryNavActions,
      items: tertiaryNavItems,
      leftActions: tertiaryNavLeftActions,
      onItemMenuClick: handleItemMenuClick,
    }),
    [handleItemMenuClick, navigationAriaLabel, tertiaryNavActions, tertiaryNavItems, tertiaryNavLeftActions]
  );

  useTertiaryNavigationRegistration(tertiaryNavigationState);

  const dialogTitleText = isCreatingNewView ? 'Create Summary View' : 'Edit Summary';
  const dialogSaveLabel = isCreatingNewView ? 'Create' : 'Save';

  // Build kebab menu items consistent with the original FirmSummary behavior:
  // - Always show Clone
  // - When the definition is editable (canEditDefinition), prepend Edit Summary and append Delete
  const renderMenuItems = () => {
    if (!menuDefinition) {
      return null;
    }
    const isEditable = canEditDefinition(menuDefinition);
    const items: React.ReactNode[] = [];

    if (isEditable) {
      items.push(
        <PrimaryNavigationMenuItem
          key="edit-summary"
          icon={<Edit fontSize="inherit" />}
          onClick={() => {
            handleMenuClose();
            openColumnEditor(menuDefinition);
          }}
          disabled={isSaving || isEditorSaving}>
          Edit Summary
        </PrimaryNavigationMenuItem>
      );
    }

    items.push(
      <PrimaryNavigationMenuItem
        key="clone"
        icon={<ContentCopy fontSize="inherit" />}
        onClick={() => handleCloneDefinition(menuDefinition.id)}
        disabled={isSaving}>
        Clone
      </PrimaryNavigationMenuItem>
    );

    if (isEditable) {
      items.push(
        <Divider key="divider" />,
        <PrimaryNavigationMenuItem
          key="delete"
          icon={<Delete fontSize="inherit" />}
          onClick={() => handleDeleteDefinition(menuDefinition.id)}
          disabled={isSaving}
          tone="danger">
          Delete
        </PrimaryNavigationMenuItem>
      );
    }

    return items;
  };

  return (
    <>
      {slugDefinition ? (
        <FirmSummaryBody activeDefinition={slugDefinition} summaryType={summaryType} />
      ) : null}

      <PrimaryNavigationMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuDefinition)}
        onClose={handleMenuClose}
        menuMinWidth={0}
        menuWidth="content"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {renderMenuItems()}
      </PrimaryNavigationMenu>

      <CurrencyMenu
        data-testid="firm-summary-currency-menu"
        anchorEl={currencyMenuAnchorEl}
        currencies={currencies}
        isCurrencyLoading={isCurrencyLoading}
        onClose={handleCurrencyMenuClose}
        onCurrencyChange={handleCurrencyChange}
        showMore={showMore}
        showLess={showLess}
        usedCurrencies={usedCurrencies}
      />

      <Dialog maxWidth="md" fullWidth open={isColumnEditorOpen} onClose={closeColumnEditor}>
        <DialogTitle>
          {dialogTitleText}
          {isCreatingNewView && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Name your view, choose which columns to display, and drag to reorder them.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={editorName}
            onChange={event => setEditorName(event.target.value)}
            disabled={isEditorSaving}
            error={isColumnEditorOpen && editorName.trim().length === 0}
            helperText={isColumnEditorOpen && editorName.trim().length === 0 ? 'Name is required.' : ' '}
          />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Columns
          </Typography>
          <EditColumnsPanel
            availableColumns={availableValueTypes.filter(vt => SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(vt.id))}
            allValueTypes={valueTypeById}
            columns={editorColumns}
            requiredColumns={requiredColumnsForEditor}
            onChange={setEditorColumns}
            disabled={isEditorSaving}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            Sort order
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 1 }}>
            Sorting uses the company&apos;s total values across all funds, not individual fund-level values.
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddSortKey}
            disabled={isEditorSaving || editorColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Sort Key
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderSortKeys}>
              <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                {editorSortKeys.map(([column, direction], index) => {
                  const sortId = sortIds[index];

                  return (
                    <SortableListItem key={sortId} id={sortId} disabled={isEditorSaving}>
                      <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                        <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                          <InputLabel id={`sort-column-${index}`}>Column</InputLabel>
                          <Select
                            labelId={`sort-column-${index}`}
                            label="Column"
                            value={column}
                            onChange={event => handleUpdateSortColumn(index, event.target.value as ColType)}
                            disabled={isEditorSaving}>
                            {editorColumns.map(col => (
                              <MenuItem key={`sort-col-${col}`} value={col}>
                                {valueTypeById.get(col)?.displayName ?? col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                          <InputLabel id={`sort-direction-${index}`}>Direction</InputLabel>
                          <Select
                            labelId={`sort-direction-${index}`}
                            label="Direction"
                            value={direction}
                            onChange={event => handleUpdateSortDirection(index, event.target.value as SortKey[1])}
                            disabled={isEditorSaving}>
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                          </Select>
                        </FormControl>

                        <IconButton
                          onClick={() => handleRemoveSortKey(index)}
                          disabled={isEditorSaving}
                          aria-label="Remove sort key"
                          sx={{ flexShrink: 0 }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </SortableListItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('sort-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('sort-', ''), 10);
                    const [column, direction] = editorSortKeys[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 220,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} ({direction === 'asc' ? 'Asc' : 'Desc'})
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            Filters
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 1 }}>
            Filtering uses the company&apos;s total values across all funds, not individual fund-level values.
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddFilter}
            disabled={isEditorSaving || editorColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Filter
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderFilters}>
              <SortableContext items={filterIds} strategy={verticalListSortingStrategy}>
                {editorFilters.map(([column, [op, value]], index) => {
                  const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
                  const filterOps = filterOptionsPerFilter[index] ?? [];
                  return (
                    <SortableListItem
                      // eslint-disable-next-line react/no-array-index-key -- filters may be duplicated (same column/op/value), so index is the only stable DnD identity
                      key={`filter-${index}`}
                      id={`filter-${index}`}
                      disabled={isEditorSaving}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFilter(index)}
                          disabled={isEditorSaving}
                          aria-label="Remove filter">
                          <Delete fontSize="small" />
                        </IconButton>
                      }>
                      <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%' }}>
                        <FormControl fullWidth size="small" sx={{ width: 160 }}>
                          <InputLabel id={`filter-column-${index}`}>Column</InputLabel>
                          <Select
                            labelId={`filter-column-${index}`}
                            label="Column"
                            value={column}
                            onChange={event => handleUpdateFilterColumn(index, event.target.value as ColType)}
                            disabled={isEditorSaving}>
                            {editorColumns.map(col => (
                              <MenuItem key={`filter-col-${col}`} value={col}>
                                {valueTypeById.get(col)?.displayName ?? col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ width: 130 }}>
                          <InputLabel id={`filter-op-${index}`}>Condition</InputLabel>
                          <Select
                            labelId={`filter-op-${index}`}
                            label="Condition"
                            value={op}
                            onChange={event => handleUpdateFilterOp(index, event.target.value as FilterOp)}
                            disabled={isEditorSaving}>
                            {filterOps.map(option => (
                              <MenuItem key={`filter-op-${option.value}`} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FilterValueInput
                          value={value}
                          formatType={formatType}
                          disabled={isEditorSaving}
                          onChange={newValue => handleUpdateFilterValue(index, newValue)}
                          onValidationChange={valid => handleFilterValidationChange(index, valid)}
                        />
                      </Stack>
                    </SortableListItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('filter-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('filter-', ''), 10);
                    const [column, [op, value]] = editorFilters[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    const dragFormatType = getDisplayFormatTypeForColumn(column, valueTypeById);
                    const dragOps = getFilterOpsForColumnType(dragFormatType);
                    const opLabel = dragOps.find(o => o.value === op)?.label ?? op;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 240,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} {opLabel} {String(value || '')}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnEditor} disabled={isEditorSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveColumns}
            variant="contained"
            disabled={
              isEditorSaving ||
              editorColumns.length === 0 ||
              editorName.trim().length === 0 ||
              isBreakevenMultipleInvalid ||
              hasInvalidFilters
            }>
            {dialogSaveLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth="xs" fullWidth open={Boolean(removeConfirm)} onClose={handleCancelRemove}>
        <DialogTitle>Remove dependent columns?</DialogTitle>
        <DialogContent dividers>
          {removeConfirm ? (
            <List dense>
              <ListItem>
                <ListItemText
                  primary={valueTypeById.get(removeConfirm.columnType)?.displayName ?? removeConfirm.columnType}
                  secondary="Selected column"
                />
              </ListItem>
              {removeConfirm.dependents.map(dependent => (
                <ListItem key={dependent}>
                  <ListItemText
                    primary={valueTypeById.get(dependent)?.displayName ?? dependent}
                    secondary="Dependent column"
                  />
                </ListItem>
              ))}
            </List>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default FirmSummaryWorkspace;
